import { FC } from 'react';

import { formatCurrency } from '@/domains/core/localization/currency';
import { getCurrency, isB2C } from '@/domains/core/settings/utils';
import { useProductCoupon } from '@/domains/loyalty/components/Coupon/hooks/useProductCoupon/useProductCoupon';
import { TableCell } from '@/domains/productDiscovery/ProductPage/common/components/BulkPurchaseTable/components/TableCell';
import { TableRow } from '@/domains/productDiscovery/ProductPage/common/components/BulkPurchaseTable/components/TableRow';
import { useBulkPurchaseTableTracking } from '@/domains/productDiscovery/ProductPage/common/components/BulkPurchaseTable/hooks/useBulkPurchaseTableTracking';
import {
  discount,
  quantity as quantityLabel,
  unitPrice as unitPriceLabel,
} from '@/domains/productDiscovery/ProductPage/common/components/BulkPurchaseTable/translations';
import { useBulkPurchase } from '@/domains/productDiscovery/ProductPage/common/modules/BulkPurchase/hooks/useBulkPurchase';
import { useQuantity } from '@/domains/productDiscovery/ProductPage/hooks/useQuantity';
import { useDetail } from '@/productDiscovery/ProductPage/hooks/useDetail';
import { type XBorderVat } from '@/productDiscovery/ProductPage/hooks/useXBorderVat';
import {
  allTaxExcludedShort,
  allTaxIncludedShort,
} from '@/shoppingCart/CartPage/MainCart/components/PayBox/translations';

import styles from './BulkPurchaseTable.module.scss';

interface Props {
  vatStatus?: XBorderVat;
}
export const BulkPurchaseTable: FC<Props> = ({ vatStatus }) => {
  const quantity = useQuantity();
  const {
    price: { primaryPrice },
    productId = 0,
  } = useDetail();
  const includesTax = isB2C() || vatStatus !== 'nonDeductible';
  const unitPrice = includesTax
    ? primaryPrice.amountWithVat
    : primaryPrice.amountWithoutVat || 0;
  const taxLabel = includesTax ? allTaxIncludedShort : allTaxExcludedShort;

  const { type, otherCouponsAvailable } = useProductCoupon(
    'product page',
    productId,
    quantity,
    unitPrice,
  );

  const isOtherCouponAvailable =
    type === 'bulk-purchase-by-seller' && otherCouponsAvailable.length > 0;

  const bulkPurchaseRecord = useBulkPurchase(productId, quantity, unitPrice);

  const ref = useBulkPurchaseTableTracking({
    selectedIndex: bulkPurchaseRecord?.selectedThresholdIndex ?? -1,
    hasCoupon: isOtherCouponAvailable,
  });

  if (!bulkPurchaseRecord?.thresholds.length) {
    return null;
  }

  const { selectedThresholdIndex: selectedIndex, thresholds } =
    bulkPurchaseRecord;

  return (
    <table className={styles.root} ref={ref}>
      <thead>
        <TableRow>
          <TableCell tag="th" highlight>
            {quantityLabel}
          </TableCell>
          <TableCell tag="th" highlight>
            {unitPriceLabel}
          </TableCell>
          <TableCell tag="th" highlight>
            {discount}
          </TableCell>
        </TableRow>
      </thead>
      <tbody>
        {thresholds.map((item, index) => {
          const isSelected = index === selectedIndex;
          const label = `${item.minUnits}${
            item.maxUnits && item.minUnits !== item.maxUnits
              ? ` - ${item.maxUnits}`
              : ''
          }${!item.maxUnits ? '+' : ''}`;

          return (
            <TableRow
              key={`${item.minUnits}${item.maxUnits}`}
              highlight={isSelected}
            >
              <TableCell tag="td" highlight={isSelected} checked={isSelected}>
                {label}
              </TableCell>
              <TableCell tag="td" highlight={isSelected}>
                {`${formatCurrency(item.price, getCurrency())} ${taxLabel}`}
              </TableCell>
              <TableCell tag="td" highlight={isSelected}>
                {`-${item.percent}%`}
              </TableCell>
            </TableRow>
          );
        })}
      </tbody>
    </table>
  );
};
